import { ReactQueryStaleTime } from '@openx/constants/react-query.ts';
import type { UseRelatedAccountsFetch } from '@openx/types/package/PackageHookTypes.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { GetOrganizationsListDocument } from '../../../graphql/query/organizations/GetOrganizationsList.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types.ts';

export const useRelatedAccountsFetch: UseRelatedAccountsFetch = (packageList, options) => {
  const accountIds = useMemo(
    () => packageList.map(item => item.account_id).filter((item): item is string => !!item),
    [packageList]
  );

  const { data, isLoading, refetch } = useQuery({
    enabled: options?.enabled !== false && accountIds.length > 0,
    queryFn: ({ client }) =>
      getGraphQLRequestFromClient(client)(GetOrganizationsListDocument, {
        where: { exchange_account_network_id: { _in: accountIds } },
      }),
    queryKey: [QueryKeys.RELATED_ACCOUNTS, accountIds],
    staleTime: ReactQueryStaleTime.MINUTES_15,
  });

  const relatedAccounts = useMemo(() => {
    const accounts = data?.account ?? [];

    return accounts.reduce((acc, account) => {
      acc[account.exchange_account_network?.uid] = {
        currency: account.exchange_account_network_currency,
        id: account.exchange_account_network?.uid,
        name: account.name,
      };
      return acc;
    }, {});
  }, [data?.account]);

  return {
    loading: isLoading,
    refetch,
    relatedAccounts,
  };
};
